import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { graphqlRequest } from "../../../../utils/graphqlRequest";
import { FiUsers, FiClipboard, FiDollarSign, FiDownload } from "react-icons/fi";
import ContractorsTab from "./ContractorsTab";
import WorkTracking from "./WorkTracking";
import PayoutTab from "./PayoutTab";
import ContractorAbaPayRuns from "./ContractorAbaPayRuns";
import "../../styles/ContractorPayroll.css";
import { useAdminPageSearch } from "../../../../utils/useAdminPageSearch";

const GET_SHARED_DROPDOWNS = `
  query GetDropdowns($limit: Int) {
    getAllUser(limit: $limit) {
      data {
        id
        firstName
        lastName
        businessName
        email
        contact
        roles
        abnNumber
        bsb
        accountNumber
        accountName
        perHourRate
        payType
        gstType
        contractorPaymentMethod
      }
    }
    findAllProject {
      id
      name
    }
  }
`;

const GET_CONTRACTOR_PAY_DATES = `
  query ContractorPayDates {
    contractorPayrollSummary(pageSize: 200) {
      contractors {
        id
        contractorPayDate
      }
    }
  }
`;

const TABS = [
  { key: "contractors", label: "Contractors", icon: FiUsers },
  { key: "work-tracking", label: "Work Tracking", icon: FiClipboard },
  { key: "payout", label: "Payout", icon: FiDollarSign },
  { key: "aba-pay-runs", label: "ABA Pay Runs", icon: FiDownload },
];

const ContractorPayroll = ({ onGoToAddContractor = () => {} }) => {
  const { get, patch } = useAdminPageSearch();
  const urlTab = get("tab");
  const activeTab = TABS.some((t) => t.key === urlTab) ? urlTab : "contractors";
  const [sharedUsers, setSharedUsers] = useState([]);
  const [sharedProjects, setSharedProjects] = useState([]);
  const [sharedLoading, setSharedLoading] = useState(true);
  const [sharedError, setSharedError] = useState("");

  const cookieCompanyName = Cookies.get("companyName") || "";
  const localCompanyName = localStorage.getItem("companyName") || "";
  const rawCompanyName = (cookieCompanyName || localCompanyName || "").trim();
  const normalizedCompanyName = rawCompanyName.toLowerCase();
  const companyName = rawCompanyName && !normalizedCompanyName.includes("admin")
    ? rawCompanyName
    : "Company";

  const companyInitials = useMemo(() => {
    const parts = companyName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }, [companyName]);

  useEffect(() => {
    const loadSharedData = async () => {
      try {
        setSharedLoading(true);
        setSharedError("");
        const [data, summaryData] = await Promise.allSettled([
          graphqlRequest(GET_SHARED_DROPDOWNS, { limit: 1000 }),
          graphqlRequest(GET_CONTRACTOR_PAY_DATES),
        ]);
        const users = data.status === "fulfilled" ? (data.value?.getAllUser?.data || []) : [];
        const projects = data.status === "fulfilled" ? (data.value?.findAllProject || []) : [];
        // Merge contractorPayDate from summary into users
        const payDateMap = {};
        if (summaryData.status === "fulfilled") {
          (summaryData.value?.contractorPayrollSummary?.contractors || []).forEach((c) => {
            if (c.id && c.contractorPayDate) payDateMap[c.id] = c.contractorPayDate;
          });
        }
        const merged = users.map((u) => payDateMap[u.id] ? { ...u, contractorPayDate: payDateMap[u.id] } : u);
        setSharedUsers(merged);
        setSharedProjects(projects);
        if (data.status === "rejected") {
          setSharedError(data.reason?.message || "Failed to load contractor payroll data.");
        }
      } catch (err) {
        setSharedUsers([]);
        setSharedProjects([]);
        setSharedError(err?.message || "Failed to load contractor payroll data.");
      } finally {
        setSharedLoading(false);
      }
    };
    loadSharedData();
  }, []);

  return (
    <div className="contractor-layout">
      <aside className="contractor-sidebar">
        <div className="contractor-brand">
          <div className="company-badge">
            <span className="company-initials">{companyInitials}</span>
            <span className="company-name">{companyName}</span>
          </div>
          <div className="contractor-brand-sub">Payroll System</div>
        </div>
        <div className="contractor-nav">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                className={`contractor-nav-item ${activeTab === t.key ? "active" : ""}`}
                onClick={() =>
                  patch({ tab: t.key === "contractors" ? "" : t.key })
                }
              >
                <Icon size={14} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </aside>

      <div className="contractor-content">
        {activeTab === "contractors" && (
          <ContractorsTab
            sharedUsers={sharedUsers}
            sharedLoading={sharedLoading}
            sharedError={sharedError}
            onGoToAddContractor={onGoToAddContractor}
          />
        )}
        {activeTab === "work-tracking" && (
          <WorkTracking
            sharedUsers={sharedUsers}
            sharedProjects={sharedProjects}
            sharedLoading={sharedLoading}
            sharedError={sharedError}
          />
        )}
        {activeTab === "payout" && (
          <PayoutTab
            sharedUsers={sharedUsers}
            sharedProjects={sharedProjects}
            sharedLoading={sharedLoading}
            sharedError={sharedError}
          />
        )}
        {activeTab === "aba-pay-runs" && <ContractorAbaPayRuns />}
      </div>
    </div>
  );
};

export default ContractorPayroll;
